import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { CreditAccountViewStore } from "../../store";
import { CreditAccountView } from "../view/CreditAccountView";
import { CreditAccountUpdateStore } from "../../store";
import { CreditAccountUpdate } from "../update/CreditAccountUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type CreditAccountModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const CreditAccountModalContent: React.FC<CreditAccountModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new CreditAccountUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new CreditAccountViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <CreditAccountView
            creditAccountViewStore={storeInfo.store as CreditAccountViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <CreditAccountUpdate
            creditAccountUpdateStore={
              storeInfo.store as CreditAccountUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
